import { ColumnDef } from "@tanstack/react-table"
import { GenericResource, IPaginatedResponse } from "models/generic"
import React, { memo, startTransition, useCallback, useDeferredValue, useEffect, useMemo, useState, useTransition } from "react"
import { Link, Route, useHistory, useParams } from "react-router-dom"
import { TablePage, TablePageProps } from "./TablePage"
import { IStorePath, useStore, useStoreActions, useStoreState } from "hooks"
import useInfiniteQueryGeneric, { UseInfiniteQueryGenericProps } from "hooks/useInfiniteQueryGeneric"
import { Button, Dropdown, Menu } from "antd"
import SearchInput from "components-old/common/SettingsPage/SearchInput"
import TableHelp, { TableHelpProps } from "./TableHelp"
import SegmentDropdown, { SegmentDropdownProps } from "./SegmentDropdown"
import { session } from "store"
import { exportTableData, tableFilterLocalFn } from "./table.helper"
import { useAtom } from "jotai"
import { isMobileAtom } from "atoms/app.atom"
import MobileHeader from "components-old/header/MobileHeader"
import usePrivacy from "hooks/usePrivacy"
import PageLayout, { PageLayoutProps } from "../page/PageLayout"
import ColumnsDropdown from "./ColumnsDropdown"
import useLocalStorage from "react-use/lib/useLocalStorage"
import { getTableActionsColumn } from "./tableActionsColumn"
import { IS_AGENT_APP, isAldeia } from "hooks/useAldeia"
import { settingsCategoriesRecords, settingsRecords } from "pages/settings/settings.def"
import { cn } from "lib/utils"
import Tooltip from "components-old/Tooltip"
import { SegmentGroupDropdowns } from "./SegmentGroupDropdowns"
import SorterDropdown from "./SorterDropdown"
import Separator from "components/navigation/Separator"
import MenuLink from "components-old/menu/MenuLink"
import SubMenu from "antd/lib/menu/SubMenu"
import { Csv02Icon, Download02Icon, Xls02Icon } from "components-old/icons"
import Empty from "./Empty"

export interface TablePageRouterProps<T extends GenericResource> extends TablePageProps<T> {
  routeBase?: string // base path for row links
  EditComponent: any
  storePath?: IStorePath
  filters?: Record<string, any>
  columns: ColumnDef<T>[]
  defaultColumns?: string[]
  defaultSegments?: Record<string, string>
  rowActions?: {
    removeDescription?: string
    hideClone?: boolean | ((row: T) => boolean)
    hideRemove?: boolean | ((row: T) => boolean)
    hideEdit?: boolean
    hideActions?: boolean
    removeOnClick?: (removeItem: () => any, row: T) => void
    getExtraActions?: (data: T, context: { updateLocal: (updatedResource: T) => void }) => React.ReactNode[]
  }
  helpText?: TableHelpProps
  newText?: string
  queryKey: string[]
  local?: boolean
  // segments?: SegmentDropdownProps<T>["segments"]
  getSegments?: (data: T[]) => SegmentDropdownProps<T>["segments"] | SegmentDropdownProps<T>["segments"][]
  usePrivacy?: boolean
  permissionFilterFn?: (item: T) => boolean
  filterFn?: (item: T, filters: Record<string, any>) => boolean
  notShowNewButton?: boolean
  defaultSorting?: (a: T, b: T) => number
  customSegments?: React.ReactNode
  overscan?: number
  appendDataItems?: T[]
  rowOnClick?: (row: T) => void
  rowOnClickContext?: (row: T, e: any) => void
  countOnColumnIndex?: number
  onFilterData?: (data: T[]) => void
  sideCollapsible?: boolean
  settingsKey?: keyof typeof settingsRecords
  layout?: "static" | "default" | "grid"
  rowHeight?: number
  searchPlaceholder?: string
  sorters?: { id: string; label: string; sorter: (a: T, b: T) => number }[]
  fixedData?: T[]
  backTo?: string
  hideNewButton?: boolean
  showHeader?: boolean
  className?: string
  gridElement?: React.ComponentType<{ item: T }>
}

function TablePageRouter<T extends GenericResource>(props: TablePageRouterProps<T> & PageLayoutProps) {
  const { routeBase, columns, ...rest } = props
  const { id } = useParams<{ id: string }>()
  const { fetchAll, setAll, saveAllPositions, setTotalCount, remove } = useStoreActions(props.storePath!)
  //@ts-ignore
  const { all, totalCount } = useStoreState(props.storePath as any)
  const history = useHistory()
  const defaultLimit = 0 // Defina o limite de itens por busca
  const [_search, setSearch] = useState<string>("")
  const [sorterId, setSorterId] = useState<string>(props.sorters?.[0]?.id || "")
  const [filters, setFilters] = useState<Record<string, any>>({ segment: "", ...(props.defaultSegments || {}) })
  const { user, workspace } = useStore(session)
  const [visibleColumns, setVisibleColumns] = useLocalStorage<string[]>(props.queryKey?.[0], props.defaultColumns)
  const [isMobile] = useAtom(isMobileAtom)
  const sorters = props.sorters

  useEffect(() => {
    startTransition(() => {
      setFilters((f) => ({ ...f, search: _search }))
    })
  }, [_search])

  const { filterPrivatedItems } = usePrivacy<T>()

  // FETCHER
  const fetchData: UseInfiniteQueryGenericProps<T>["fetchData"] = useCallback(async () => {
    // await timeout(1000)

    // return {
    //   data: [],
    //   totalCount: 0,
    // }

    if (props.fixedData) {
      return { data: props.fixedData, totalCount: props.fixedData.length }
    }

    const paginatedResponse: IPaginatedResponse<T> = await fetchAll({
      limit: defaultLimit,
      offset: 0,
      config: { reset: true },
      ...props.filters,
    })

    return { data: paginatedResponse.data, totalCount: paginatedResponse.totalCount }
  }, [fetchAll, props.filters, props.fixedData])

  // REACT QUERY
  const { data, fetchNextPage, isFetching, isLoading, addLocal, updateLocal, upsertLocal, removeLocal, setAllLocal } =
    useInfiniteQueryGeneric<T>({
      key: props.queryKey,
      fetchData,
      onSuccess: props.onSuccess,
      // params: {
      //   conversationId: props.conversationId,
      // },
      enabled: !props.fixedData,
      initialData: { pages: [{ data: props.fixedData || all || [], nextPage: 0, total: totalCount }], pageParams: [null] },
    })

  // const deferredPages = useDeferredValue(data?.pages)

  // FIRST FILTERING
  const { flatDataRaw } = React.useMemo(() => {
    let page = data?.pages?.flatMap((page) => page.data) ?? []

    if (props.usePrivacy) {
      page = filterPrivatedItems(page)
    }

    if (props.permissionFilterFn) {
      page = page.filter((item) => props.permissionFilterFn?.(item))
    }

    return { flatDataRaw: page }
  }, [data?.pages?.[0]?.data, data?.pages?.[0]?.data?.length, filterPrivatedItems, props.usePrivacy, props.fixedData])

  // const flatDataRaw = useDeferredValue(_flatDataRaw)

  const segmentCounts = useMemo(() => {
    let counts = {}
    const allSegments = props.getSegments?.(flatDataRaw ?? []) || []

    if (!allSegments?.length) {
      return {}
    }

    const calculateSegmentCount = (segment: any, index: number, item: any) => {
      segment?.forEach((segment) => {
        // console.log(segment.value, segment.filteredTotal, item, segment?.filterFn?.(item))
        counts[`${segment.value}-${index}`] =
          (counts[`${segment.value}-${index}`] || 0) + (segment.filteredTotal ?? segment?.filterFn?.(item) ?? 0)
      })
    }

    for (let i = 0; i < flatDataRaw.length; i++) {
      const item = flatDataRaw[i]

      Array.isArray(allSegments?.[0])
        ? allSegments.forEach((segment, index) => {
            calculateSegmentCount(segment, index, item)
          })
        : calculateSegmentCount(allSegments, 0, item)
    }
    return counts
  }, [flatDataRaw?.length])

  // FILTERING
  const { flatData, isFiltering, segments } = React.useMemo(() => {
    let page = flatDataRaw
    const segments = props.getSegments?.(page) || []

    if (props.local) {
      page = tableFilterLocalFn(
        { filters, page, filterFn: props.filterFn },
        { user, workspace, segments: Array.isArray(segments?.[0]) ? (segments as any) : [segments as any] },
      )
    }
    // if (props.usePrivacy) {
    //   page = filterPrivatedItems(page)
    // }

    const isFiltering = Object.values(filters).some((v) => !!v)
    let flatData = props.appendDataItems ? [...page, ...props.appendDataItems] : page
    if (!props.defaultSorting && !sorters?.length && !!(flatData?.[0] as any)?.name && !props.dnd) {
      flatData = flatData.sort((a, b) => {
        try {
          if (a?.createdAt || (a as any)?.lastCreatedAt) {
            const aDate = new Date(a?.createdAt || (a as any).lastCreatedAt)
            const bDate = new Date(b?.createdAt || (b as any).lastCreatedAt)
            return bDate.getTime() - aDate.getTime()
          }
        } catch (e) {
          console.log(e)
        }

        return (a as any).name.localeCompare((b as any).name)
      })
    }

    const result = { flatData: props.defaultSorting ? flatData.sort(props.defaultSorting) : flatData, isFiltering: !!isFiltering, segments }

    props.onFilterData?.(result.flatData)

    const sorter = sorters?.find((s) => s.id === sorterId)

    return { ...result, flatData: sorter ? result.flatData.sort(sorter.sorter) : result.flatData }
  }, [filters, user, workspace, flatDataRaw, sorters?.length, sorterId])

  // COLUMNS
  const { columnsMemo, tableActionsColumn } = useMemo(() => {
    const visibleColumnsFinal = props.defaultColumns
      ? columns?.filter((c) => {
          return visibleColumns?.includes(c.id!)
        })
      : columns

    const tableActionsColumn = getTableActionsColumn({ routeBase, rowActions: props.rowActions, updateLocal, remove, removeLocal })

    let cols: ColumnDef<T>[] = [...visibleColumnsFinal, ...(props.rowActions?.hideActions ? [] : [tableActionsColumn])]

    if (isMobile) {
      // Leave only first and last columns
      cols = [cols[0], cols[cols.length - 1]]
    }

    const columnsMemo: ColumnDef<T>[] = [
      ...cols?.map((col, i) => {
        return {
          ...(col as any),
          ...(i === (props.countOnColumnIndex || 0)
            ? {
                header: (info) => {
                  let header
                  if (typeof col.header === "string") {
                    header = col.header
                  } else {
                    header = (col.header as any)?.()
                  }

                  return `${header}${flatData?.length ? ` (${flatData.length})` : ""}`
                },
              }
            : {}),
          cell: (info) => {
            if (props.rowOnClick) {
              return (
                <a
                  className="flex items-center w-full h-full"
                  onClick={(e) => {
                    e?.stopPropagation?.()
                    return props.rowOnClick?.(info?.row.original!)
                  }}
                  {...(props.rowOnClickContext ? { onContextMenu: (e) => props.rowOnClickContext?.(info?.row.original!, e) } : {})}
                >
                  {(col.cell as any)?.(info)}
                </a>
              )
            }

            return (
              <Link
                to={`${props.routeBase}/${info?.row.original?.id}`}
                className="flex items-center w-full h-full"
                {...(props.rowOnClickContext
                  ? {
                      onContextMenu: (e) => {
                        props.rowOnClickContext?.(info?.row.original!, e)
                      },
                    }
                  : {})}
              >
                {(col.cell as any)?.(info)}
              </Link>
            )
          },
        }
      }),
    ]

    return { columnsMemo, tableActionsColumn }
  }, [columns, flatData.length, isMobile, props.defaultColumns, props.routeBase, visibleColumns])

  const isStatic = props.layout === "static"
  const isGrid = props.layout === "grid"

  const actionNew = props.hideNewButton ? null : (
    <Link to={`${props.routeBase}/new`} className="global-add-button-link">
      {isStatic ? (
        <Button type="link" icon={<IconMaterialSymbolsAdd className="!size-[18px]" />} className="h-8 btn-white">
          {isMobile ? undefined : props.newText || "Novo"}
        </Button>
      ) : (
        <Button type="primary" icon={<i className="far fa-plus text-xs mt-0.5 mobile:text-lg" />}>
          {isMobile ? undefined : props.newText || "Novo"}
        </Button>
      )}
    </Link>
  )

  const showSegmentComponent = !!segments?.length || !!props.customSegments || !!props.defaultColumns
  const segmentsComponent = showSegmentComponent ? (
    <>
      {!!segments?.length && (
        <SegmentGroupDropdowns
          segmentGroups={Array.isArray(segments?.[0]) ? (segments as any) : [segments as any]}
          filters={filters}
          setFilters={setFilters}
          data={flatData}
          segmentCounts={segmentCounts}
          totalCount={flatDataRaw?.length}
        />
      )}
      {/* {!!segments?.length && (
        <SegmentDropdown<T>
          selected={filters.segment}
          setSelected={(value) => {
            setFilters((f) => ({ ...f, segment: value }))
          }}
          segments={segments}
        />
      )} */}
      {props.customSegments}
      {!!props.defaultColumns && (
        <ColumnsDropdown
          columns={props.columns}
          setVisibleColumns={setVisibleColumns}
          visibleColumns={visibleColumns || []}
          defaultColumns={props.defaultColumns}
        />
      )}
    </>
  ) : null

  const showBefore = !!segmentsComponent || !!props.sorters || !!props.getExportableData

  const top = isMobile ? null : (
    <TablePageRouterTop
      isStatic={isStatic}
      isGrid={isGrid}
      helpText={props.helpText}
      searchPlaceholder={props.searchPlaceholder}
      setSearch={setSearch}
      setFilters={setFilters}
      segmentsComponent={segmentsComponent}
      sorters={sorters}
      notShowNewButton={props.notShowNewButton}
      actionNew={actionNew}
      top={props.top}
    />
  )

  // const settingsRecord = settingsRecords[props.settingsKey as keyof typeof settingsRecords]

  let title =
    isAldeia && props.settingsKey && !isStatic ? (
      <div className="flex gap-3 items-center">
        {/* {
          <i
            className={cn(
              settingsCategoriesRecords[settingsRecord.category].className,
              "fc size-8 rounded-xl [&_path]:stroke-2 [&_svg]:size-[18px]",
            )}
          >
            {settingsRecord.icon}
          </i>
        } */}
        {props.title}
      </div>
    ) : props.backTo ? (
      <div className="flex gap-2 items-center">
        <Link to={props.backTo}>
          <Button icon={<IconMaterialSymbolsChevronLeft className="opacity-80 !size-5 shrink-0" />} className="size-8 !rounded-full" />
        </Link>
        {props.title}
      </div>
    ) : (
      props.title
    )

  if (IS_AGENT_APP) {
    title = <div className="mt-14 text-3xl">{title}</div>
  }

  return (
    <PageLayout title={props.title} permission={props.permission} className={props.className}>
      {isMobile && (
        <>
          <MobileHeader title={props.title} backTo="/settings" className="global-settings-mobile-header" />
          {actionNew}
        </>
      )}
      <TablePage
        {...rest}
        routeBase={props.routeBase}
        extra={isStatic ? undefined : props.extra}
        getExportableData={isStatic ? undefined : props.getExportableData}
        title={isMobile ? "" : title}
        onSuccess={(payload) => {
          setAll(payload.pages[0].data)
          setTotalCount(payload.pages[0].total)
        }}
        emptyComponent={
          props.hideNewButton || !isAldeia ? undefined : (
            <>
              <div className="animate-fade-up animate-duration-200">
                <Empty className="w-52 h-52" />
              </div>
              <div className="mt-2 font-semibold text-center text-md">Nada encontrado</div>
              <div className="max-w-[300px] text-center">Você pode adicionar registro clicando no botão abaixo</div>
              <Link to={`${props.routeBase}/new`} className="mt-4">
                <Button icon={<IconMaterialSymbolsAddRounded />} type={isAldeia ? "primary" : "default"}>
                  Adicionar
                </Button>
              </Link>
            </>
          )
        }
        actions={
          isStatic ? (
            <>
              {actionNew}
              {/* <Tooltip title="Ajuda" side="bottom">
                <Button
                  type="text"
                  icon={
                    <i className="fc">
                      <IconMageQuestionMarkCircle className="!size-[17px] [&_path]:stroke-2" />
                    </i>
                  }
                  className="h-8 btn-white size-8"
                ></Button>
              </Tooltip> */}
            </>
          ) : undefined
        }
        tableProps={{
          data: flatData,
          totalCount: totalCount,
          fetchNextPage: fetchNextPage,
          isFetching: isFetching,
          isLoading: isLoading,
          columns: columnsMemo,
          onClickRow: (row) => {
            if (props.rowOnClick) {
              return props.rowOnClick(row)
            } else {
              history.push(`${routeBase}/${row.id}`)
            }
          },
          isFiltering,
          transparentHeader: true,
          local: props.local,
          rowHeight: props.rowHeight,
          onSaveNewPositions: (data) => {
            setAll(data)
            setAllLocal(data)
            saveAllPositions(data)
          },
          overscan: props.overscan,
        }}
        top={top}
        tableActionsColumn={tableActionsColumn}
        before={
          isStatic && showBefore ? (
            <div className="flex gap-2 justify-between items-center py-2 mid g_table_segments">
              {segmentsComponent ? <div className="flex gap-2 items-center">{segmentsComponent}</div> : <div />}
              {(props.sorters || !!props.getExportableData || !!props.extra?.length) && (
                <div className="flex gap-0 items-center">
                  {/* <Button
                    type="text"
                    className={cn(
                      "rounded-xl size-8 opacity-50 hover:opacity-100",
                      isFiltering && "!opacity-100 !bg-selected-lighter",
                    )}
                    icon={<IconTablerColumns className="!size-[18px]" />}
                  ></Button> */}

                  {props.sorters && <SorterDropdown sorters={props.sorters} selected={sorterId} setSelected={setSorterId} />}
                  {(props.getExportableData || !!props.extra?.length) && (
                    <Dropdown
                      key={"drop"}
                      placement="bottomRight"
                      overlay={
                        <Menu>
                          {props.extra?.map((e) => {
                            if (e.custom) {
                              return e.custom
                            }
                            if (e.separator) {
                              return <Separator key={e.id || "separator-menu"} />
                            }
                            if (e.to) {
                              return (
                                <Menu.Item key={e.id} disabled={e.disabled}>
                                  <MenuLink iconName={e.iconName} to={e.to}>
                                    {e.title}
                                  </MenuLink>
                                </Menu.Item>
                              )
                            }
                            return (
                              <Menu.Item key={e.id} icon={<i className={e.iconName} />} disabled={e.disabled} onClick={e.onClick}>
                                {e.title}
                              </Menu.Item>
                            )
                          })}
                          {!!props.getExportableData && (
                            <SubMenu
                              key="export"
                              icon={
                                <i>
                                  <Download02Icon />
                                </i>
                              }
                              title="Exportar"
                            >
                              <Menu.Item
                                key="export-csv"
                                icon={
                                  <i>
                                    <Csv02Icon />
                                  </i>
                                }
                                onClick={() => {
                                  exportTableData(flatData, props.getExportableData!)
                                }}
                              >
                                CSV
                              </Menu.Item>
                              <Menu.Item
                                key="export-xlsx"
                                icon={
                                  <i>
                                    <Xls02Icon />
                                  </i>
                                }
                                onClick={async () => {
                                  exportTableData(flatData, props.getExportableData!, { xls: true })
                                }}
                              >
                                Excel
                              </Menu.Item>
                            </SubMenu>
                          )}
                        </Menu>
                      }
                      trigger={["click"]}
                    >
                      <Button
                        type="text"
                        className={cn("rounded-xl size-7 opacity-50 hover:opacity-100", isFiltering && "!opacity-100 !bg-selected-lighter")}
                        icon={<IconMageDots className="!size-[18px]" />}
                      ></Button>
                    </Dropdown>
                  )}
                </div>
              )}
            </div>
          ) : undefined
        }
        cardLayoutClassName={cn(props.showHeader ? "g_show_header" : undefined, IS_AGENT_APP && "overflow-auto scroll-thin")}
      />
      {!!props.EditComponent && (
        <Route
          path={`${props.routeBase}/:id/:panel?/:stageId?`}
          // component={props.EditComponent}
          render={(renderProps) => (
            <props.EditComponent
              {...renderProps}
              onSubmitSuccess={(data, onClose) => {
                upsertLocal(data)
                // if (id === "new") {
                //   addLocal(data)
                // } else {
                //   updateLocal(data)
                // }
                onClose?.()
                // saveAllPositions(data)
                // onClose()
              }}
            />
          )}
        />
      )}
    </PageLayout>
  )
}

const TablePageRouterTop = memo(
  (props: {
    isStatic?: boolean
    isGrid?: boolean
    helpText?: TableHelpProps
    searchPlaceholder?: string
    // searchValue?: string
    setSearch: (value: string) => void
    segmentsComponent?: React.ReactNode
    notShowNewButton?: boolean
    actionNew?: React.ReactNode
    top?: React.ReactNode
    filters?: Record<string, any>
    sorters?: TablePageRouterProps<any>["sorters"]
    setFilters: (filters: Record<string, any>) => void
  }) => {
    const [search, setSearch] = useState("")

    const deferredSearch = useDeferredValue(search)

    useEffect(() => {
      props.setSearch(deferredSearch)
    }, [deferredSearch])

    return (
      <div>
        {/* Help Text */}
        {props.helpText && !isAldeia && <TableHelp {...props.helpText} />}
        <div className={cn("flex justify-between items-center py-4 aldeia:px-[14px] aldeia:pt-0 g_table_top", IS_AGENT_APP && "!pb-2")}>
          <div className="flex flex-1 gap-2">
            <SearchInput
              gray
              icon
              className={cn(
                "max-w-xs w-full !rounded-xl",
                props.isGrid &&
                  "!bg-base-100 aldeia:!outline !outline-[4px] !outline-solid !outline-selected-light !h-12 !rounded-full [&_input]:!h-12 [&_input]:!max-h-12 !border-transparent",
              )}
              autoFocus
              value={search}
              onChange={(e) => {
                // startTransition(() => {
                //   // setFilters((f) => ({ ...f, search: e?.target?.value }))
                // })
                setSearch(e?.target?.value)
              }}
              placeholder={props.searchPlaceholder}
            />
            {!props.isStatic && props.segmentsComponent}
            {/* {isFiltering ? (
        <Button
          type="text"
          icon={
            // <i className="far fa-sliders-h" />
            // <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
            <FilterRemoveIcon />
            //   <path fill="currentColor" d="M10 18v-2h4v2zm-4-5v-2h12v2zM3 8V6h18v2z"></path>
            // </svg>
          }
          // className="border border-solid shadow border-divider-100"
          onClick={() => {
            setFilters({})
          }}
          className="rounded-xl text-primary hover:text-primary"
        >
          Limpar filtros
        </Button>
      ) : (
        <Button
          type="text"
          icon={
            // <i className="far fa-sliders-h" />
            // <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
            <FilterIcon />
            //   <path fill="currentColor" d="M10 18v-2h4v2zm-4-5v-2h12v2zM3 8V6h18v2z"></path>
            // </svg>
          }
          // className="border border-solid shadow border-divider-100"
          // className={twMerge(isFiltering && "shadow")}
          className="rounded-xl"
        >
          Filtros
        </Button>
      )} */}
          </div>
          {!props.notShowNewButton && !props.isStatic && <div>{props.actionNew}</div>}
        </div>
        {props.top}
      </div>
    )
  },
)

export default TablePageRouter
